import { Loader2 } from 'lucide-react';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { INSTRUMENT_HEADINGS, contractSummary, type SummaryInstrument } from '../activationSummary';
import { activationVerdict, type SetupSnapshot, type SetupState } from '../setupState';
import { BRAND_BUTTON } from './NarratorLine';
import { gateFooter } from './manifestView';
import type { Activation } from './useActivation';

interface Props {
    state: SetupState;
    snapshot: SetupSnapshot;
    activation: Activation;
}

/**
 * The confirm surface, mounted ONCE by the page.
 *
 * It is deliberately not part of the footer below: the footer is drawn twice on a narrow
 * viewport (once in the column, once inside the journey sheet), and a dialog rendered from
 * there would open two copies of itself over each other bound to the same open flag.
 */
export function ActivationConfirm({ state, snapshot, activation }: Props) {
    const summary = contractSummary(snapshot);

    /**
     * THE CONFIRM REFUSES TOO, AND NOT ONLY THE BUTTON THAT OPENS IT.
     *
     * The narrator's `Activate contract` action calls `openConfirm` directly, so a surface that
     * relied on the footer's disabled state for its guarantee would still submit through the band.
     * `can_activate` is the server's own answer to the question this dialog's action asks — see
     * `gateFooter` for why relaying it is not the client second-guessing the server, and for the
     * window in which this is the ONLY thing enforcing [D-B14] until `20260802030000` teaches
     * `sign_contract` to refuse on the same list.
     *
     * THROUGH `activationVerdict`, WHICH IS THE THIRD AND LAST SURFACE TO CALL IT. The band, the
     * pinned footer and this dialog now ask one function whether activation may be offered; the
     * band used to answer for itself, and answered differently.
     */
    const blocked = activationVerdict(state.gate).blocked;
    const blockers = state.gate.blockers;

    return (
        <AlertDialog
            open={activation.confirmOpen}
            onOpenChange={(open) => (open ? activation.openConfirm() : activation.closeConfirm())}
        >
            <AlertDialogContent className="sd-tokens max-h-[85vh] max-w-2xl overflow-y-auto">
                <AlertDialogHeader>
                    <AlertDialogTitle>Activate contract #{snapshot.contractId}?</AlertDialogTitle>
                    <AlertDialogDescription>
                        This moves the contract out of DRAFT and the driver starts owing repayments.
                        Read the contract back before it goes live — this is what it prints.
                    </AlertDialogDescription>
                </AlertDialogHeader>

                <div className="sd-clauses">
                    {summary.clauses.map((clause) => (
                        <div className="sd-clause" key={clause.no}>
                            <span className="sd-cl-no">{clause.no}</span>
                            <p className="sd-cl-text">
                                {clause.parts.map((piece, index) => {
                                    if (piece.kind === 'text') return <span key={index}>{piece.text}</span>;
                                    return (
                                        <span
                                            key={index}
                                            className={cn(
                                                'sd-cl-val',
                                                piece.kind === 'missing' && 'sd-cl-val-missing',
                                            )}
                                        >
                                            {piece.text}
                                        </span>
                                    );
                                })}
                            </p>
                        </div>
                    ))}
                </div>

                {/* ONE BLOCK PER INSTRUMENT [D-B18]. The witness's name and phone are the witness
                    agreement's required fields now, not the hire purchase agreement's, and a
                    proof-read that ran them together would be asserting something neither template
                    says. An instrument with nothing to show draws no heading at all — and the last
                    block is not an instrument at all, but the group for the odometer reading, which
                    activation requires and no template prints. */}
                {(Object.keys(INSTRUMENT_HEADINGS) as SummaryInstrument[]).map((instrument) => {
                    const facts = summary.facts.filter((fact) => fact.instrument === instrument);
                    if (facts.length === 0) return null;
                    return (
                        <div key={instrument}>
                            {/* THE KEYS ARE THE ORDER, THE SUMMARY IS THE WORDS [D-B45]. Block
                                order is a property of this surface and never of the contract; the
                                insurance heading is not — it names whichever of that type's two
                                published instruments the premium year is actually on. */}
                            <p className="sd-hint">{summary.headings[instrument]}</p>
                            <div className="sd-rows">
                                {facts.map((fact) => (
                                    <div className="sd-rec" key={fact.field}>
                                        <div className="sd-rec-k">{fact.label}</div>
                                        <div className={cn('sd-rec-v sd-num', !fact.value && 'sd-empty')}>
                                            {fact.value ?? (fact.derived ? 'Set when the agreement is generated' : '—')}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}

                {summary.missing.length > 0 && (
                    <p className="sd-hint text-[hsl(var(--destructive))]">
                        {/* "This activation" rather than "these agreements": the odometer reading is
                            counted here and is printed by no instrument. */}
                        {summary.missing.length === 1
                            ? '1 field this activation requires is empty: '
                            : `${summary.missing.length} fields this activation requires are empty: `}
                        {summary.missing.map((fact) => fact.label.toLowerCase()).join(', ')}.
                    </p>
                )}

                {blocked && blockers.length > 0 && (
                    <div className="sd-hint text-[hsl(var(--destructive))]">
                        {blockers.length === 1
                            ? 'The server will not activate this contract yet, for one reason:'
                            : `The server will not activate this contract yet, for ${blockers.length} reasons:`}
                        <ul className="mt-1 list-disc pl-5">
                            {/* As `spokenBlockers` settled them: the server's own sentences, with
                                the driver-profile arm narrowed to the fields actually absent. */}
                            {blockers.map((blocker) => (
                                <li key={blocker.code}>{blocker.message}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        className={BRAND_BUTTON}
                        onClick={(event) => {
                            // The dialog would close itself on click; the mutation decides when it
                            // closes, so a refusal does not leave the admin staring at a dismissed
                            // surface with no explanation.
                            event.preventDefault();
                            activation.submit();
                        }}
                        disabled={activation.pending || blocked}
                    >
                        {activation.pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Activate contract
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}

/**
 * The pinned activation control, and the proof-read in front of it.
 *
 * IT HAS BEEN THERE ALL WEEK. The control is visible from day one reading `4 gates unmet`, so
 * the final gate is never a surprise at the end of a wizard — and it stays clickable, because
 * the client does not own the gate list. Pressing it with gates outstanding is a legitimate way
 * to ask the server what it thinks, and the answer lands verbatim on the rows it belongs to.
 * The two states it refuses in are the two where pressing cannot help: a seal in flight, and a
 * contract that has already left DRAFT.
 *
 * The confirm surface is a PROOF-READ, not a speed bump: every field the contract instruments
 * declare required, under the instrument that prints each [D-B18] — plus the odometer reading,
 * which activation requires and no instrument prints — with the four money values inside the clause
 * sentences they print as. The last chance anybody has to notice that the weekly
 * instalment is a decimal point out — or that the wrong person has been named as witness — is the
 * moment before the contract goes live.
 */
export default function ActivateGate({ state, activation }: Omit<Props, 'snapshot'>) {
    const gateCount = state.rows.filter((row) => row.cls === 'gate').length;
    const footer = gateFooter(state.gate, gateCount, activation.activatedAt);

    return (
        <>
            <div
                className={cn(
                    'sd-gate-note',
                    footer.tone === 'ready' && 'sd-gate-note-ready',
                    footer.tone === 'bad' && 'sd-gate-note-bad',
                )}
            >
                {state.gate.sealPending && <Loader2 className="h-3 w-3 animate-spin" />}
                {footer.note}
            </div>

            <Button
                className="w-full"
                disabled={footer.disabled || activation.pending}
                onClick={footer.intent === 'receipt' ? activation.openReceipt : activation.openConfirm}
            >
                {activation.pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {activation.pending ? 'Activating…' : footer.label}
            </Button>
        </>
    );
}
